import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { resetPasswordSchema, type ResetPasswordFormValues } from '@emazy/shared-validation';

import { buildLoginRoute } from '@/config/routes';
import {
  FormErrorSummary,
  PasswordField,
  PasswordRequirements,
  SubmitButton,
} from '@/features/auth/components';
import { useAuth } from '@/features/auth/context';
import { getAuthErrorMessage } from '@/features/auth/utils';
import { useDocumentMetadata } from '@/hooks/useDocumentMetadata';

export function ResetPasswordPage() {
  useDocumentMetadata('Reset Password');
  const [searchParams] = useSearchParams();
  const { resetPassword } = useAuth();
  const [completedEmail, setCompletedEmail] = useState<string>();
  const [error, setError] = useState<string>();
  const {
    register,
    handleSubmit,
    control,
    resetField,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      email: searchParams.get('email') ?? '',
      code: searchParams.get('code') ?? '',
      newPassword: '',
      confirmPassword: '',
    },
  });
  const password = useWatch({ control, name: 'newPassword', defaultValue: '' });

  async function submit(values: ResetPasswordFormValues) {
    setError(undefined);
    try {
      await resetPassword({
        email: values.email,
        code: values.code,
        newPassword: values.newPassword,
      });
      resetField('newPassword');
      resetField('confirmPassword');
      setCompletedEmail(values.email);
    } catch (caught) {
      setError(getAuthErrorMessage(caught));
    }
  }

  if (completedEmail)
    return (
      <div className="auth-page auth-success">
        <i className="pi pi-check-circle" aria-hidden="true" />
        <p className="eyebrow">Password reset</p>
        <h1>Your password is ready</h1>
        <p>Sign in with the new password to continue.</p>
        <Link
          className="button-link button-link--primary"
          to={buildLoginRoute({ email: completedEmail })}
        >
          Continue to sign in
        </Link>
      </div>
    );

  return (
    <div className="auth-page">
      <p className="eyebrow">Choose a fresh password</p>
      <h1>Reset your password</h1>
      <p className="auth-page__intro">
        Enter the six-digit code and choose a password that meets every requirement.
      </p>
      <FormErrorSummary message={error} />
      <form className="auth-form" noValidate onSubmit={handleSubmit(submit)}>
        <label className="auth-field" htmlFor="reset-email">
          <span>Email</span>
          <input
            id="reset-email"
            className="p-inputtext p-component"
            type="email"
            autoComplete="email"
            {...register('email')}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'reset-email-error' : undefined}
          />
          {errors.email && (
            <small id="reset-email-error" role="alert">
              {errors.email.message}
            </small>
          )}
        </label>
        <label className="auth-field" htmlFor="reset-code">
          <span>Reset code</span>
          <input
            id="reset-code"
            className="p-inputtext p-component code-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            {...register('code')}
            aria-invalid={Boolean(errors.code)}
            aria-describedby={errors.code ? 'reset-code-error' : undefined}
          />
          {errors.code && (
            <small id="reset-code-error" role="alert">
              {errors.code.message}
            </small>
          )}
        </label>
        <PasswordField
          id="new-password"
          label="New password"
          autoComplete="new-password"
          registration={register('newPassword')}
          error={errors.newPassword?.message}
        />
        <PasswordRequirements password={password} />
        <PasswordField
          id="confirm-new-password"
          label="Confirm new password"
          autoComplete="new-password"
          registration={register('confirmPassword')}
          error={errors.confirmPassword?.message}
        />
        <SubmitButton label="Reset password" loading={isSubmitting} />
      </form>
    </div>
  );
}
